import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Button } from '@treezor/design-system';

import { RadioCard } from '@/ui-kit/RadioCard/RadioCard';
import { BrandingIcon } from '@/ui-kit/BrandingIcon/BrandingIcon';
import { TBrandingIconName } from '@/ui-kit/BrandingIcon/BrandingIcon.types';
import { useDashboardConfig } from '@/hooks/useDashboardConfig';

import { IPostUser } from '@/features/user/store/user.model';
import { useCreateUserContext } from '@/features/user/contexts/CreateUserContext';
import { CreateUserContentContainer } from '@/features/user/components/CreateUser/layout/CreateUserContentContainer';

import { TUserCreationType } from '@/store/models/dashboard-config.model';

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const RadioContainer = styled.div`
  display: grid;
  grid-template-columns: min-content min-content min-content;
  gap: ${({ theme }) => theme.spacing[6]};
`;

const Footer = styled.div`
  display: flex;
  justify-content: end;
`;

export const UserTypeSelection = () => {
  const dashboardConfig = useDashboardConfig();
  const { t } = useTranslation();

  const userCreationTypes = dashboardConfig.settings.users?.creation;

  const [selectedUserType, setSelectedUserType] = useState<TUserCreationType>(userCreationTypes?.[0]);

  const { updateUser, selectUserType } = useCreateUserContext();

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const parentId = queryParams.get('parentId');
    if (parentId) {
      selectUserType(selectedUserType);
      updateUser({ parentUserId: Number(parentId), userTypeId: 1, isAttachedToParent: 'yes' });
    }
  }, [selectUserType, selectedUserType, updateUser]);

  const UserList: Record<
    TUserCreationType,
    {
      icon: TBrandingIconName;
      title: string;
      description: string;
    }
  > = {
    physical: {
      icon: 'user',
      title: t('pages.dashboard.userCreation.userTypes.physical_user'),
      description: t('pages.dashboard.userCreation.description.physical_user_description'),
    },
    business: {
      icon: 'enterprise',
      title: t('pages.dashboard.userCreation.userTypes.business_user'),
      description: t('pages.dashboard.userCreation.description.business_user_description'),
    },
    selfEmployed: {
      icon: 'business_center_v1',
      title: t('pages.dashboard.userCreation.userTypes.selfEmployed_user'),
      description: t('pages.dashboard.userCreation.description.selfEmployed_user_description'),
    },
    parent: {
      icon: 'parent_child',
      title: t('pages.dashboard.userCreation.userTypes.parent_child_user'),
      description: t('pages.dashboard.userCreation.description.parent_child_user_description'),
    },
    nonGovernmental: {
      icon: 'non_governmental_organization',
      title: t('pages.dashboard.userCreation.userTypes.nonGovernmental_user'),
      description: t('pages.dashboard.userCreation.description.nonGovernmentald_user_description'),
    },
    governmental: {
      icon: 'government_v1',
      title: t('pages.dashboard.userCreation.userTypes.governmental_user'),
      description: t('pages.dashboard.userCreation.description.governmental_user_description'),
    },
  };

  const getUpdatedData = useCallback((): IPostUser => {
    switch (selectedUserType) {
      case 'physical':
      case 'parent':
        return { userTypeId: 1 };
      case 'business':
        return { userTypeId: 2 };
      case 'selfEmployed':
        return { userTypeId: 1, isSelfEmployed: true };
      case 'nonGovernmental':
        return { userTypeId: 3 };
      case 'governmental':
        return { userTypeId: 4 };
      default:
        return {};
    }
  }, [selectedUserType]);

  const handleSelectUserType = useCallback(() => {
    selectUserType(selectedUserType);

    const updateData: IPostUser = getUpdatedData();
    updateUser(updateData);
  }, [selectUserType, getUpdatedData, updateUser, selectedUserType]);

  return (
    <CreateUserContentContainer isCenter>
      <Header>
        <h3 className="typo-h3-bold">{t('pages.dashboard.userCreation.title')}</h3>
        <span className="typo-body-regular-paragraphLarge">{t('pages.dashboard.userCreation.description.main')}</span>
      </Header>
      <RadioContainer>
        {userCreationTypes.map((userType: TUserCreationType) => {
          const { icon, title, description } = UserList[userType];

          return (
            <RadioCard
              key={userType}
              icon={<BrandingIcon name={icon} size="xl" />}
              title={title}
              description={description}
              value={userType}
              isSelected={userType === selectedUserType}
              handleChange={(value) => setSelectedUserType(value as TUserCreationType)}
            />
          );
        })}
      </RadioContainer>
      <Footer>
        <Button onClick={handleSelectUserType}>{t('pages.dashboard.userCreation.actions.continue')}</Button>
      </Footer>
    </CreateUserContentContainer>
  );
};
